import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { useTenant } from "hooks/settings";

interface UseTabNavigationOptions<T extends string> {
  tabSpecs: { key: T }[];
  defaultTabKey?: T;
  basePath?: string;
  paramName?: string;
}

export const useTabNavigation = <T extends string>({
  tabSpecs,
  defaultTabKey,
  basePath,
  paramName = "tabKey",
}: UseTabNavigationOptions<T>) => {
  const [selectedTabKey, setSelectedTabKey] = useState(tabSpecs[0]?.key);
  const params = useParams();
  const navigate = useNavigate();
  const tenant = useTenant();

  const tabKey = params[paramName] as T | undefined;
  const activeTabKey = tabKey ?? defaultTabKey ?? tabSpecs[0]?.key;

  useEffect(() => {
    if (!tabKey && defaultTabKey && basePath) {
      void navigate(`/${tenant}${basePath}/${defaultTabKey}`);
    }
  }, [tabKey, navigate, tenant, defaultTabKey, basePath]);

  const handleTabChange = async (newTabKey: T) => {
    setSelectedTabKey(newTabKey);
    if (newTabKey && basePath) {
      await navigate(`/${tenant}${basePath}/${newTabKey}`);
    }
  };

  return {
    selectedTabKey: basePath ? activeTabKey : selectedTabKey,
    setSelectedTabKey: basePath ? handleTabChange : setSelectedTabKey,
    activeTabKey,
    notFound: tabSpecs.find((spec) => spec.key === tabKey) === undefined,
  };
};
